import React, { useState } from 'react';
import { Icon } from '../UI/Icon';
import { useTranslation } from 'react-i18next';

/**
 * Component per la gestione del certificato NIS
 * Mostra anteprima e permette il download del PDF
 */
export default function NisCertificateModal({ companyId, onClose }) {
  const { t, i18n } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL;
  const currentLang = i18n.language?.substring(0, 2) || 'it';
  const previewUrl = `${API_BASE}/legal-compliance/api/conformita/${companyId}/nis/certificato/preview/?lang=${currentLang}`;
  const pdfUrl = `${API_BASE}/legal-compliance/api/conformita/${companyId}/nis/certificato.pdf?lang=${currentLang}`;

  const handleTogglePreview = async () => {
    if (!showPreview && !previewHtml) {
      // Carica l'anteprima la prima volta
      setIsLoadingPreview(true);
      try {
        const token = sessionStorage.getItem('access_token');
        const response = await fetch(previewUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Errore nel caricamento dell\'anteprima');
        }

        const html = await response.text();
        setPreviewHtml(html);
        setShowPreview(true);
      } catch (error) {
        console.error('Errore caricamento anteprima:', error);
        alert(t('certificate.downloadError', 'Errore durante il caricamento dell\'anteprima'));
      } finally {
        setIsLoadingPreview(false);
      }
    } else {
      setShowPreview(!showPreview);
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const response = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Errore nel download del certificato');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AutoAttestazione_NIS.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Errore download certificato:', error);
      alert(t('certificate.downloadError', 'Errore durante il download del certificato'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">
          {t('certificate.title', 'Auto-Attestazione Conformità NIS')}
        </h2>
        <p className="text-gray-200 text-sm">
          {t('certificate.description', 'Il questionario NIS è completato al 100%. Puoi scaricare il certificato ufficiale.')}
        </p>
      </div>

      {/* Preview - altezza ridotta */}
      {showPreview && previewHtml && (
        <div className="rounded-xl overflow-hidden bg-white shadow-2xl border border-gray-200">
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 px-5 py-3 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-800 tracking-wide">
              {t('certificate.previewTitle', 'ANTEPRIMA CERTIFICATO')}
            </span>
            <button
              onClick={() => setShowPreview(false)}
              className="text-gray-800 hover:text-gray-600 transition-colors"
              title="Chiudi anteprima"
            >
              <Icon name="close" />
            </button>
          </div>
          <div className="bg-gray-50 p-2">
            <iframe 
              srcDoc={previewHtml}
              className="w-full border-0 bg-white rounded-lg shadow-inner"
              style={{ height: '400px' }}
              title="Certificate Preview"
            />
          </div>
        </div>
      )}

      {/* Actions - SOTTO l'anteprima */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        {/* Preview Button */}
        <button
          onClick={handleTogglePreview}
          disabled={isLoadingPreview}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-colors duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
        >
          {isLoadingPreview ? (
            <>
              <Icon name="loading" className="animate-spin" />
              <span>{t('certificate.loading', 'Caricamento...')}</span>
            </>
          ) : (
            <>
              <Icon name={showPreview ? 'eyeslash' : 'eye'} />
              <span>{showPreview ? t('certificate.hidePreview', 'Nascondi Anteprima') : t('certificate.showPreview', 'Mostra Anteprima')}</span>
            </>
          )}
        </button>

        {/* Download PDF Button */}
        <button
          onClick={handleDownloadPdf}
          disabled={isDownloading}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-colors duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
        >
          {isDownloading ? (
            <>
              <Icon name="loading" className="animate-spin" />
              <span>{t('certificate.downloading', 'Download in corso...')}</span>
            </>
          ) : (
            <>
              <Icon name="download" />
              <span>{t('certificate.download', 'Scarica PDF')}</span>
            </>
          )}
        </button>
      </div>

      {/* Info footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
        <div className="flex gap-2">
          <Icon name="info" className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900 mb-1">
              {t('certificate.infoTitle', 'Informazioni sul Certificato')}
            </p>
            <ul className="space-y-1 text-gray-600">
              <li>• {t('certificate.info1', 'Il certificato include il radar delle categorie NIS completate')}</li>
              <li>• {t('certificate.info2', 'Ogni certificato ha un codice di verifica univoco')}</li>
              <li>• {t('certificate.info3', 'Il certificato è stampabile in formato A4')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Close button */}
      <div className="flex justify-center pt-4">
        <button
          onClick={onClose}
          className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('common.close', 'Chiudi')}
        </button>
      </div>
    </div>
  );
}
